import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // The token is kept in sessionStorage rather than localStorage. localStorage
  // survives closing and reopening the browser/PWA entirely, which meant
  // anyone starting the app fresh landed straight on the dashboard without
  // ever seeing the login screen. sessionStorage is cleared the moment the
  // browser tab / installed app is closed, so every fresh start requires
  // signing in again — for owner, admin, and sales rep alike — while still
  // keeping the session alive across normal page refreshes/navigation while
  // the app stays open.
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => { sessionStorage.removeItem("token"); })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    // Simple email+password login — backend returns the token directly.
    const { data } = await api.post("/auth/login", { email, password });
    sessionStorage.setItem("token", data.token);
    setUser(data.user);
    return data.user;
  };

  // Re-fetches the current user from the server and updates context — used after the
  // Owner edits their own name/email/password so the header reflects it immediately
  // without needing to log out and back in.
  const refreshUser = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  const logout = () => { sessionStorage.removeItem("token"); setUser(null); };

  return <AuthCtx.Provider value={{ user, loading, login, logout, refreshUser }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);